// src/components/landing/Testimonials.jsx
import React, { useState } from 'react';

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Medical Student',
      avatar: '👩‍⚕️',
      content: 'IntelliNote AI has revolutionized how I study. The flashcard generator saves me hours each week, and the AI summarizer helps me grasp complex medical concepts quickly.',
      rating: 5,
    },
    {
      name: 'Michael Chen',
      role: 'Software Engineer',
      avatar: '👨‍💻',
      content: 'As a developer, I attend countless meetings. The Zoom integration and automatic note summarization have been game-changers for my productivity.',
      rating: 5,
    },
    {
      name: 'Dr. Emily Rodriguez',
      role: 'University Professor',
      avatar: '👩‍🏫',
      content: 'I recommend IntelliNote to all my students. The AI search feature helps them find relevant information across all their notes instantly.',
      rating: 5,
    },
    {
      name: 'James Wilson',
      role: 'Product Manager',
      avatar: '👨‍💼',
      content: 'The team collaboration features have transformed how we document meetings and share knowledge. The ROI has been incredible.',
      rating: 4,
    },
    {
      name: 'Priya Patel',
      role: 'Law Student',
      avatar: '👩‍⚖️',
      content: 'Summarizing legal cases used to take hours. Now, I can do it in minutes with IntelliNote AI. The accuracy is impressive.',
      rating: 5,
    },
  ];

  const stats = [
    { value: '4.9/5', label: 'Average Rating' },
    { value: '10K+', label: 'Active Users' },
    { value: '98%', label: 'Retention Rate' },
    { value: '24/7', label: 'AI Support' },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Loved by Students & Professionals
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Join thousands of users who have transformed their note-taking experience
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                {stat.value}
              </div>
              <div className="text-gray-600 dark:text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Testimonial Card */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Avatar & Info */}
              <div className="text-center md:text-left">
                <div className="text-6xl mb-4">{testimonials[activeIndex].avatar}</div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {testimonials[activeIndex].name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{testimonials[activeIndex].role}</p>
                
                {/* Rating */}
                <div className="flex justify-center md:justify-start mb-6">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-6 h-6 ${
                        i < testimonials[activeIndex].rating
                          ? 'text-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              
              {/* Testimonial Content */}
              <div className="flex-1">
                <div className="text-6xl text-gray-200 dark:text-gray-700 mb-4">"</div>
                <p className="text-xl text-gray-700 dark:text-gray-300 italic mb-8">
                  {testimonials[activeIndex].content}
                </p>
                
                {/* Navigation Dots */}
                <div className="flex justify-center space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveIndex(index)}
                      className={`w-3 h-3 rounded-full transition ${
                        index === activeIndex
                          ? 'bg-blue-600'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={() => setActiveIndex((activeIndex - 1 + testimonials.length) % testimonials.length)}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-12 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => setActiveIndex((activeIndex + 1) % testimonials.length)}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-12 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Trust Badges */}
        <div className="mt-24">
          <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
            Trusted by students and professionals at leading institutions
          </p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 items-center justify-center">
            {['🏫', '🎓', '💼', '🏥', '⚖️', '🔬'].map((icon, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-2">{icon}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {['Universities', 'Graduate Students', 'Corporations', 'Medical', 'Legal', 'Research'][index]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;